import { useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CFormSwitch,
  CSpinner,
  CStepper,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilLockLocked } from "@coreui/icons";
import {
  CommunicationCategory,
  CommunicationChannel,
  useCommunicationPreferences,
  useUpdateCommunicationPreferences,
  type CommunicationPreference,
} from "../../api/hooks/useCommunicationPreferences";
import { useToast } from "../../context/useToast";
import { useFailure } from "../../context/useFailure";
import type { ApiError } from "../../api/client";
import { routePaths } from "../../routes/route-paths";

// Mirrors CStepper's exposed imperative API, as ProfileEditWizard does.
interface CStepperRef {
  next: () => void;
  prev: () => void;
  finish: () => void;
  reset: () => void;
}

const CATEGORY_COPY: Record<number, { name: string; detail: string }> = {
  [CommunicationCategory.AccountAndSecurity]: {
    name: "Account and security",
    detail: "Verifying your email, your sign-in details, password resets.",
  },
  [CommunicationCategory.Subscriptions]: {
    name: "Your subscriptions",
    detail: "Renewals, payment problems, cancellations.",
  },
  [CommunicationCategory.Signals]: {
    name: "Signals from your strategies",
    detail: "Every signal from your strategies, as it arrives.",
  },
  [CommunicationCategory.StrategyRequests]: {
    name: "Strategy requests you made",
    detail: "When we publish a strategy you asked for, or decide not to build it.",
  },
  [CommunicationCategory.ProductUpdates]: {
    name: "New strategies and product updates",
    detail: "Strategies we publish, and changes to the portal worth knowing about.",
  },
  [CommunicationCategory.Offers]: {
    name: "Offers",
    detail: "Discounts and partner offers.",
  },
};

const CHANNEL_NAMES: Record<number, string> = {
  [CommunicationChannel.Email]: "Email",
};

/** Stable across renders, and what the switch state is keyed by. */
const keyOf = (p: { category: number; channel: number }) => `${p.category}:${p.channel}`;

export function CommunicationPreferencesWizard() {
  const navigate = useNavigate();
  const { showSuccess, showWarning } = useToast();
  const { showFailure, clearFailure } = useFailure();
  const preferences = useCommunicationPreferences();
  const updatePreferences = useUpdateCommunicationPreferences();

  const stepperRef = useRef<CStepperRef>(null);
  const [, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [chosen, setChosen] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (preferences.data) {
      setChosen(
        Object.fromEntries(
          preferences.data.filter((p) => p.optional).map((p) => [keyOf(p), p.allowed]),
        ),
      );
    }
  }, [preferences.data]);

  const all = preferences.data ?? [];

  // Columns come from what the API offered rather than from the enum: a channel nobody is offered
  // yet would otherwise sit there as a column of dashes.
  const channels = [...new Set(all.map((p) => p.channel))].sort((a, b) => a - b);
  const categories = [...new Set(all.map((p) => p.category))];
  const byPair = new Map(all.map((p) => [keyOf(p), p]));

  const nameOf = (category: number) => CATEGORY_COPY[category]?.name ?? `Category ${category}`;
  const detailOf = (category: number) => CATEGORY_COPY[category]?.detail ?? "";
  const channelName = (channel: number) => CHANNEL_NAMES[channel] ?? `Channel ${channel}`;

  const handleSubmit = async () => {
    setSubmitting(true);
    clearFailure();
    try {
      // The whole set, not what changed — anything left out is turned off. An empty array is a real
      // request meaning "nothing optional", so it is sent rather than skipped.
      await updatePreferences.mutateAsync(
        all
          .filter((p) => p.optional && chosen[keyOf(p)])
          .map((p) => ({ category: p.category, channel: p.channel })),
      );
      showSuccess("Communication preferences saved.");
      navigate(routePaths.profile);
    } catch (err) {
      const apiError = err as ApiError;
      const message =
        err instanceof Error
          ? err.message
          : "We couldn't save your preferences right now. Please try again in a moment.";

      if (apiError?.status !== undefined && apiError.status < 500) {
        showWarning(message);
      } else {
        showFailure(message, apiError?.traceId);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const switchCell = (preference: CommunicationPreference | undefined) => {
    if (!preference) {
      return <span className="text-body-secondary">—</span>;
    }

    // Shown rather than hidden, and locked rather than a switch we ignore: these are part of the
    // service, and a customer who could not use what they paid for is a support ticket, not a
    // preference honoured.
    if (!preference.optional) {
      return (
        <span className="text-body-secondary small text-nowrap">
          <CIcon icon={cilLockLocked} className="me-1" />
          Always on
        </span>
      );
    }

    return (
      <CFormSwitch
        className="d-inline-block m-0"
        aria-label={`${nameOf(preference.category)} by ${channelName(preference.channel)}`}
        checked={chosen[keyOf(preference)] ?? false}
        onChange={(e) => setChosen({ ...chosen, [keyOf(preference)]: e.target.checked })}
      />
    );
  };

  const reviewCell = (preference: CommunicationPreference | undefined) => {
    if (!preference) {
      return <span className="text-body-secondary">—</span>;
    }

    if (!preference.optional) {
      return <span className="text-body-secondary">Always sent</span>;
    }

    const on = chosen[keyOf(preference)] ?? false;
    return <span className={on ? "text-success" : "text-body-secondary"}>{on ? "On" : "Off"}</span>;
  };

  /* The same table in both steps: the review is a read-back of what was just set, so changing its
     shape would make the customer re-read a different thing rather than confirm the same one. */
  const table = (cell: (p: CommunicationPreference | undefined) => ReactNode) => (
    <CTable align="middle" responsive className="mb-4">
      <CTableHead>
        <CTableRow>
          <CTableHeaderCell scope="col">What we send</CTableHeaderCell>
          {channels.map((channel) => (
            <CTableHeaderCell key={channel} scope="col" className="text-end">
              {channelName(channel)}
            </CTableHeaderCell>
          ))}
        </CTableRow>
      </CTableHead>
      <CTableBody>
        {categories.map((category) => (
          <CTableRow key={category}>
            <CTableDataCell>
              <div className="fw-semibold">{nameOf(category)}</div>
              <div className="text-body-secondary small">{detailOf(category)}</div>
            </CTableDataCell>
            {channels.map((channel) => (
              <CTableDataCell key={channel} className="text-end">
                {cell(byPair.get(`${category}:${channel}`))}
              </CTableDataCell>
            ))}
          </CTableRow>
        ))}
      </CTableBody>
    </CTable>
  );

  const steps = [
    {
      label: "Choose",
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">Choose</h4>
          </CCardHeader>
          <CCardBody>
            {preferences.isLoading && <CSpinner />}
            {preferences.isError && (
              <CAlert color="warning">
                We couldn't load your preferences right now. Please try again in a moment.
              </CAlert>
            )}

            {all.length > 0 && table(switchCell)}

            <div className="d-flex justify-content-between">
              <CButton
                color="secondary"
                variant="outline"
                onClick={() => navigate(routePaths.profile)}
              >
                Cancel
              </CButton>
              <CButton
                color="primary"
                onClick={() => stepperRef.current?.next()}
                disabled={all.length === 0}
              >
                Continue
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      ),
    },
    {
      label: "Review",
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">Review</h4>
          </CCardHeader>
          <CCardBody>
            <p className="text-body-secondary">Check this is right, then save.</p>

            {table(reviewCell)}

            <div className="d-flex justify-content-between">
              <CButton
                color="secondary"
                variant="outline"
                onClick={() => stepperRef.current?.prev()}
                disabled={submitting}
              >
                Back
              </CButton>
              <CButton color="primary" onClick={handleSubmit} disabled={submitting}>
                {submitting && <CSpinner size="sm" className="me-2" />}
                Save preferences
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      ),
    },
  ];

  return (
    <>
      <h2>Communication preferences</h2>
      <p className="text-body-secondary">Choose which emails you get from us.</p>
      <hr />

      <CStepper
        ref={stepperRef}
        steps={steps}
        onStepChange={setCurrentStep}
        onReset={() => setCurrentStep(1)}
      />
    </>
  );
}

export default CommunicationPreferencesWizard;
